import jStat from 'jstat';
import { round6 } from 'utilities/math';
import {
  GREATER_THAN_VALUE,
  LESS_THAN_VALUE
} from 'scenes/InferentialStatistics/Constants';

const baseFns = {
  getStErrors: (s1, s2, sd1, sd2) => {
    return {
      standardErrorFirst: sd1 / Math.sqrt(s1),
      standardErrorSecond: sd2 / Math.sqrt(s2)
    };
  },
  makeOneSidedPValue: (tStat, df) => {
    return jStat.studentt.cdf(-Math.abs(tStat), df);
  },
  makeTwoSidedPValue: (tStat, df) => {
    return jStat.studentt.cdf(-Math.abs(tStat), df) * 2;
  },
  getPValue: (tStat, df, alternativeHypothesis) => {
    switch (alternativeHypothesis) {
      case GREATER_THAN_VALUE:
        return tStat > 0
          ? baseFns.makeOneSidedPValue(tStat, df)
          : 1 - baseFns.makeOneSidedPValue(tStat, df);
      case LESS_THAN_VALUE:
        return tStat < 0
          ? baseFns.makeOneSidedPValue(tStat, df)
          : 1 - baseFns.makeOneSidedPValue(tStat, df);
      default:
        return baseFns.makeTwoSidedPValue(tStat, df);
    }
  },
  getLimits: (ste, dfMeans, ci, df) => {
    const cl = 1 - ((1 - ci) / 2);
    const zValue = jStat.studentt.inv(cl, df);
    const subst = zValue * ste;

    return {
      upperLimit: dfMeans + subst,
      lowerLimit: dfMeans - subst
    };
  }
};

const fnsWithVariance = {
  ...baseFns,
  getStDevPooled: (s1, s2, sd1, sd2) => {
    return Math.sqrt((((s1 - 1) * sd1 ** 2) + ((s2 - 1) * sd2 ** 2)) / (s1 + s2 - 2));
  },
  getStError: (s1, s2, _sd1, _sd2, sdp) => {
    return sdp * Math.sqrt((1 / s1) + (1 / s2));
  },
  getDF: (s1, s2) => {
    return s1 + s2 - 2;
  },
  getTStatistics: (m1, m2, _sd1, _sd2, s1, s2, mh, sdp) => {
    return ((m1 - m2) - mh) / (sdp * Math.sqrt((1 / s1) + (1 / s2)));
  }
};

const fnsWithoutVariance = {
  ...baseFns,
  getStDevPooled: () => {
    return 1;
  },
  getStError: (s1, s2, sd1, sd2) => {
    return Math.sqrt((sd1 ** 2 / s1) + (sd2 ** 2 / s2));
  },
  getDF: (s1, s2, sd1, sd2) => {
    return ((sd1 ** 2 / s1) + (sd2 ** 2 / s2)) ** 2 / (
      ((sd1 ** 2 / s1) ** 2 / (s1 - 1)) + (
        (sd2 ** 2 / s2) ** 2 / (s2 - 1))
    );
  },
  getTStatistics: (m1, m2, sd1, sd2, s1, s2, mh) => {
    return ((m1 - m2) - mh) / Math.sqrt((sd1 ** 2 / s1) + (sd2 ** 2 / s2));
  }
};

export const makeTwoSampleTHypothesis = ({
  sampleMeanFirst: meanFirst,
  standardDeviationFirst: stDevFirst,
  sampleSizeFirst: sizeFirst,
  sampleMeanSecond: meanSecond,
  standardDeviationSecond: stDevSecond,
  sampleSizeSecond: sizeSecond,
  assumeEqualVariances,
  alternativeHypothesis,
  hypothesisMean
}) => {
  const fns = assumeEqualVariances ? fnsWithVariance : fnsWithoutVariance;

  const dfMeans = Number(meanFirst) - Number(meanSecond);
  const stDevPooled = fns.getStDevPooled(+sizeFirst, +sizeSecond, +stDevFirst, +stDevSecond);

  const tStatistics = fns.getTStatistics(+meanFirst, +meanSecond, +stDevFirst, +stDevSecond, +sizeFirst, +sizeSecond, +hypothesisMean, +stDevPooled);
  const df = fns.getDF(+sizeFirst, +sizeSecond, +stDevFirst, +stDevSecond);
  const standardError = fns.getStError(+sizeFirst, +sizeSecond, +stDevFirst, +stDevSecond, +stDevPooled);
  const standardErrors = fns.getStErrors(+sizeFirst, +sizeSecond, +stDevFirst, +stDevSecond);
  const pValue = fns.getPValue(tStatistics, df, alternativeHypothesis);

  return {
    h0: hypothesisMean,
    h1: hypothesisMean,
    dfValue: dfMeans,
    tStatistics,
    df,
    standardError,
    standardErrors,
    pValue
  };
};

export const makeTwoSampleTConfidenceLimits = ({
  sampleMeanFirst: meanFirst,
  standardDeviationFirst: stDevFirst,
  sampleSizeFirst: sizeFirst,
  sampleMeanSecond: meanSecond,
  standardDeviationSecond: stDevSecond,
  sampleSizeSecond: sizeSecond,
  assumeEqualVariances,
  confidenceInterval
}) => {
  const fns = assumeEqualVariances ? fnsWithVariance : fnsWithoutVariance;

  const dfMeans = Number(meanFirst) - Number(meanSecond);
  const stDevPooled = fns.getStDevPooled(+sizeFirst, +sizeSecond, +stDevFirst, +stDevSecond);

  const df = fns.getDF(+sizeFirst, +sizeSecond, +stDevFirst, +stDevSecond);
  const standardError = fns.getStError(+sizeFirst, +sizeSecond, +stDevFirst, +stDevSecond, +stDevPooled);
  const standardErrors = fns.getStErrors(+sizeFirst, +sizeSecond, +stDevFirst, +stDevSecond);

  const limits = fns.getLimits(standardError, dfMeans, confidenceInterval / 100, df);

  return {
    df,
    standardErrors,
    standardError,
    dfValue: dfMeans,
    ...limits
  };
};

export const calculateTableDataForTwoSampleT = ({
  sampleMeanFirst,
  standardDeviationFirst,
  sampleSizeFirst,
  standardErrorFirst,
  sampleMeanSecond,
  standardDeviationSecond,
  sampleSizeSecond,
  standardErrorSecond,
  firstVariable,
  secondVariable
}) => {
  return [
    [firstVariable ? firstVariable : 'Sample #1', round6(sampleSizeFirst), round6(sampleMeanFirst), round6(standardDeviationFirst), round6(standardErrorFirst)],
    [secondVariable ? secondVariable : 'Sample #2', round6(sampleSizeSecond), round6(sampleMeanSecond), round6(standardDeviationSecond), round6(standardErrorSecond)]
  ];
};
